import * as React from "react";
import * as moment from "moment";
import { observer } from "mobx-react";
import { AppStore } from "../../stores/AppStore";
import { observable } from "mobx";
import { Alert, Alignment, Button, Classes, Dialog, FormGroup, InputGroup, Switch, Tabs, Tag } from "@blueprintjs/core";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { IAgendamento, IEmpresa, newEmpresa, newIAgendamento } from "../../model";
import { BootstrapTable, TableHeaderColumn, SearchField } from "react-bootstrap-table";
import { Pager } from "../utils/Pager";
import { toast } from "react-toastify";
import { EmpresaSelect, filterEmpresa, renderEmpresa } from "../empresa/EmpresaSelect";
import { EmpresaStore } from "../../stores/empresa/EmpresaStore";
import { formatCnpj, getMomentFormatter, sortBy } from "../../utils";
import { CndsStore } from "../../stores/cnds/CndsStore";
import SolicitaCnds from "./SolicitaCnds";
import { getTag } from "../utils/getStatus";
import { DateInput } from "@blueprintjs/datetime";
import MomentLocaleUtils from "react-day-picker/moment";
import LoadingModal from "../loading/LoadingModal";

  /**
 * Componente principal da página de CNDS.
 * 
 * Este componente gerencia a exibição e manipulação de dados relacionados à CNDS,
 * incluindo agendamentos, tarefas e interações do usuário.
 */
@observer
export default class CndsPage extends React.Component<{}, {}> {

  /**
 * Observável que contém os estados e dados utilizados na página.
 */
  obs = observable({
    listEmpresas: [] as any[],
    tarefas_page: 1 as number,
    selectEmpresa: newEmpresa() as IEmpresa,
    erros: "" as string,
    view_erros: false as boolean,
    open_agendamentos: false as boolean,
    open_edit_agendamento: false as boolean,
    agendamento: newIAgendamento() as IAgendamento,
    recorrente: false as boolean,
    data: null as Date
  })

  /**
 * Método do ciclo de vida do React chamado após o componente ser montado.
 */
  async componentDidMount() {
    AppStore.setTitle("Certidão Negativa de Débitos (CNDS)");
  }

  /**
 * Abre a janela de Agendamentos cadastrados.
 * 
 * Esta função inicializa a página de agendamentos e realiza a busca inicial dos agendamentos cadastrados chamando o método `handleIndexAgendamentos`.
 * Por fim, define o estado `open_agendamentos` como `true` para exibir a janela de agendamentos.
 */
  async handleOpenAgendamentos() {
    CndsStore.obs.page_agendamentos = 1
    await CndsStore.handleIndexAgendamentos(1);
    this.obs.open_agendamentos = true
  }

  /**
 * Fecha a janela de Agendamentos cadastrados.
 * 
 * Esta função altera o estado `open_edit_agendamento` para `false` para fechar a janela de edição.
 */
  async handleCloseAgendamento() {
    this.obs.open_edit_agendamento = false
    this.obs.agendamento = newIAgendamento()
  }


  /**
 * Gerencia a seleção ou deseleção de todas as linhas em uma tabela.
 * 
 * @param {boolean} check - Indica se as linhas estão sendo selecionadas (`true`) ou desmarcadas (`false`).
 * @param {array} row - Um array de objetos que representa as linhas a serem processadas.
 * 
 */
  handleRowSelectAll = async (check, row) => {
    if (check) {

      row?.map(value => {

        this.obs.agendamento.listEmpresas.push({ cnpj: value.cnpj, ie: value.ie });

      });

    } else {

      this.obs.agendamento.listEmpresas = []

    }
  };

  /**
 * Valida a data selecionada para o agendamento.
 * 
 * Este método verifica as seguintes condições:
 * - Se o agendamento é gratuito e recorrente, a data deve ser anterior ao dia atual.
 * - Se o agendamento é gratuito e não recorrente, a data deve ser anterior ao dia atual ou de um mês diferente.
 * - Se o agendamento não é gratuito e não recorrente, a data não pode ser anterior ao dia atual.
 * 
 * Em caso de validação falha, exibe um alerta com a mensagem apropriada e redefine a data do agendamento como `null`.
 * 
 * @returns {boolean} - `true` se a data for válida, `false` caso contrário.
 */
  valida_data_agendamento() {

    var string = this.obs.agendamento.data_solicitacao.toString();
    var dataAgora = moment();
    var dataSelecionada = moment(string);

    if (this.obs.agendamento.gratuito) {

      if (this.obs.agendamento.recorrente) {
        if (!dataSelecionada.isBefore(dataAgora, 'day')) {
          toast.warning('Para agendamentos gratuitos e recorrentes, a data deve ser menor que o dia de hoje.', {});
          this.obs.agendamento.data_solicitacao = null;
          return false;
        }
      } else {

        if (!dataSelecionada.isBefore(dataAgora, 'day') && dataSelecionada.month() === dataAgora.month()) {
          toast.warning('Para agendamentos gratuitos e não recorrentes, a data deve ser inferior ao dia de hoje ou de um mês diferente.', {});
          this.obs.agendamento.data_solicitacao = null;
          return false;
        }
      }

    }

    if (!this.obs.agendamento.recorrente && !this.obs.agendamento.gratuito) {

      if (dataSelecionada.isBefore(dataAgora, 'day')) {
        toast.warning('Você não pode selecionar uma data inferior ao dia de hoje.', {});
        this.obs.agendamento.data_solicitacao = null;
        return false;
      }

    }

    return true;

  }

  /**
 * Atualiza os dados de um agendamento existente.
 * 
 * Este método executa as seguintes ações:
 * 1. Verifica se a data do agendamento foi preenchida. Caso contrário, exibe um erro.
 * 2. Verifica se pelo menos uma empresa foi selecionada. Caso contrário, exibe um erro.
 * 3. Valida a data do agendamento usando `valida_data_agendamento`.
 * 4. Prepara os parâmetros necessários para a atualização do agendamento:
 *    - `recorrente`: Indica se o agendamento é recorrente.
 *    - `itens`: Lista de empresas associadas ao agendamento.
 *    - `dia`, `mes`, `ano`: Componentes da data selecionada.
 *    - `gratuito`: Indica se o agendamento é gratuito.
 *    - `id`: ID do agendamento.
 * 5. Envia os dados para a atualização via `CndsStore.handleUpdateAgendamento`.
 * 6. Fecha a interface de edição, limpa o estado do agendamento e recarrega os agendamentos.
 * 
 * @returns {Promise<void>}
 */
  async handleUpdateTask() {

    if (!(this.obs.agendamento.data_solicitacao)) {

      toast.error("Informe um dia válido!", {});
      return

    }

    if (this.obs.agendamento.listEmpresas.length == 0) {

      toast.error("Selecione pelo menos uma Empresa!", {});
      return

    }

    if (!this.valida_data_agendamento()) return

    let params = {
      recorrente: this.obs.agendamento.recorrente,
      itens: this.obs.agendamento.listEmpresas,
      dia: parseInt(moment(this.obs.agendamento.data_solicitacao).format("DD")),
      mes: parseInt(moment(this.obs.agendamento.data_solicitacao).format("MM")),
      ano: parseInt(moment(this.obs.agendamento.data_solicitacao).format("YYYY")),
      gratuito: this.obs.agendamento.gratuito,
      id: this.obs.agendamento.id
    }

    this.obs.open_edit_agendamento = false

    let res = await CndsStore.handleUpdateAgendamento(params)

    CndsStore.obs.loading_solicitacao = false

    if (res) {

      await CndsStore.handleIndexAgendamentos(1);

    }

    this.obs.agendamento = newIAgendamento()

  }

  /**
 * Realiza a busca de tarefas de forma paginada com base no número da página fornecido.
 * 
 * @param {number} page - Número da página a ser carregada.
 * 
 * Este método atualiza o estado atual da página (`this.obs.tarefas_page`). 
 * 
 * Em seguida, chama o método `handleTasksPaged` para buscar as tarefas correspondentes à página e à empresa selecionada.
 */
  handleBuscaTarefasPaged = async (page: number) => {

    this.obs.tarefas_page = page

    CndsStore.obs.loading_tasks = true

    CndsStore.handleTasksPaged(page, this.obs.selectEmpresa.cnpj);

  };

/**
 * Cria um componente de campo de busca personalizado.
 * 
 * @param {object} props - Propriedades recebidas pelo componente.
 * @param {string} props.defaultSearch - Valor padrão do campo de busca.
 * 
 * @returns {JSX.Element} - Um componente `SearchField` configurado com as classes e placeholders apropriados.
 * 
 */
  createCustomSearchField = (props) => {

    return (
      <SearchField
        className="input-filter"
        defaultValue={props.defaultSearch}
        placeholder="Filtrar (Razão Social, CNPJ)"
      />
    );

  };

  /**
 * Atualiza a página atual dos agendamentos e realiza a busca correspondente.
 * 
 * @param {number} page - Número da página a ser carregada.
 * 
 */
  handlePageAgendamento = async (page: number) => {

    CndsStore.obs.page_agendamentos = page;

    await CndsStore.handleIndexAgendamentos(page);

  };

  /**
 * Gerencia a seleção ou deseleção de uma linha específica na tabela.
 * 
 * @param {object} row - Objeto que representa a linha selecionada ou desmarcada.
 * @param {boolean} checked - Indica se a linha foi selecionada (`true`) ou desmarcada (`false`).
 * 
 */
  handleRowSelect = async (row, checked) => {
    if (checked) {

      this.obs.agendamento.listEmpresas.push({ cnpj: row.cnpj, ie: row.ie });

    } else {

      let index = this.obs.agendamento.listEmpresas.findIndex(obj => obj.cnpj === row.cnpj);

      if (index !== -1) {
        this.obs.agendamento.listEmpresas.splice(index, 1);
      }

    };
  }

  /**
 * Atualiza a empresa selecionada e inicia a busca por tarefas relacionadas.
 * 
 * @param {object} empresa - Objeto representando a empresa selecionada. Deve conter a propriedade `cnpj`.
 */
  handleBuscaTarefas = async (empresa) => {

    this.obs.selectEmpresa = empresa

    CndsStore.obs.loading_tasks = true

    CndsStore.handleTasksPaged(1, this.obs.selectEmpresa.cnpj);

  };

  /**
 * Fecha a exibição de erros e limpa o conteúdo dos erros.
 */
  onCloseErros() {
    this.obs.view_erros = false
    this.obs.erros = ""
  }

  /**
 * Atualiza a lista de tarefas paginadas com base na empresa selecionada.
 * 
 * @returns {void} Retorna um aviso se nenhuma empresa estiver selecionada. Caso contrário, busca as tarefas.
 */
  handleAtualizaTarefasPaged = async () => {

    if (this.obs.selectEmpresa.cnpj == "") {
      toast.warning('Selecione uma empresa!', {});
      return;
    }

    CndsStore.obs.loading_tasks = true

    await CndsStore.handleTasksPaged(this.obs.tarefas_page, this.obs.selectEmpresa.cnpj);

  };

  /**
 * Aplica um filtro nos agendamentos e redefine para a primeira página.
 * 
 * Este método reinicia a paginação dos agendamentos e busca os dados da primeira página.
 */
  handleFiltraAgendamentos = async () => {

    CndsStore.obs.page_agendamentos = 1;

    await CndsStore.handleIndexAgendamentos(1);

  };

  /**
 * Atualiza a data de solicitação no agendamento.
 * 
 * @param {Date|null} val - A nova data de solicitação. Se for `null`, a data será removida.
 */
  handleDateRealizarChange = (val: Date) => {
    if (!val) {
      this.obs.agendamento.data_solicitacao = null;
      return;
    }

    this.obs.agendamento.data_solicitacao = val;

  };

  /**
 * Abre a interface para criar uma nova solicitação de crédito.
 * 
 * Este método realiza a consulta de crédito e, em seguida, exibe a janela para solicitar crédito.
 */
  async handleOpenCreate() {

    await CndsStore.handleConsultaCredito();

    CndsStore.obs.open_solicitar = true

  }

  /**
 * Formata a data de um agendamento com base na recorrência.
 * 
 * @param {string} cell - Data a ser formatada.
 * @param {object} row - Objeto representando a linha.
 * @returns {string} - Data formatada como "DD" ou "DD/MM/YYYY".
 */
  formatAgendamento = (cell, row) => {

    return row.recorrente ? moment(cell).format("DD") : moment(cell).format("DD/MM/YYYY")

  }

  /**
 * Formata o valor booleano indicando se um serviço é gratuito.
 * 
 * @param {boolean} cell - Valor indicando se é gratuito.
 * @param {object} row - Objeto representando a linha.
 * @returns {string} - "SIM" se gratuito, "NÃO" caso contrário.
 */
  formatGratuito = (cell, row) => {

    return cell ? "SIM" : "NÃO"

  }

  /**
 * Formata a data de criação.
 * 
 * @param {string|null} cell - Data de criação.
 * @param {object} row - Objeto representando a linha.
 * @returns {string} - Data formatada como "DD/MM/YYYY", ou uma string vazia se não houver data.
 */
  criadoEmFormatter = (cell, row) => {

    if (!cell) return ""

    return moment(cell).format("DD/MM/YYYY");

  }

  /**
 * Prepara a edição de um agendamento.
 * 
 * @param {IAgendamento} row - Objeto representando o agendamento a ser editado.
 * 
 * Este método carrega os detalhes do agendamento selecionado (`row`) no estado atual (`this.obs.agendamento`), 
 * define o estado `open_edit_agendamento` como `true` para abrir a interface de edição.
 */
  handleEditAgendamento(row: IAgendamento) {

    this.obs.agendamento.data_solicitacao = row.data_solicitacao;

    this.obs.agendamento.listEmpresas = row.listEmpresas

    this.obs.agendamento.recorrente = row.recorrente

    this.obs.agendamento.gratuito = row.gratuito

    this.obs.agendamento.id = row.id

    this.obs.open_edit_agendamento = true

  };

  /**
 * Renderiza os botões de ações para um agendamento.
 * 
 * @param {any} cell - Célula da tabela (não utilizado).
 * @param {object} row - Objeto representando a linha.
 * @returns {JSX.Element} - Botões para editar e, opcionalmente, excluir o agendamento.
 */
  buttonFormatterDetalhesAgend = (cell, row) => {
    return (
      <>
        <MDBBtn
          color="default"
          rounded
          size="sm"
          onClick={() => this.handleEditAgendamento(row)}
        >
          <img src="/images/edit.png" width="18px" />
        </MDBBtn>
        {!row.gratuito &&
          <MDBBtn
            color="default"
            rounded
            size="sm"
            onClick={() => this.handleDeleteAgendamento(row)}
          >
            <img src="/images/bin.png" width="18px" />
          </MDBBtn>
        }
      </>
    );
  }

  /**
 * Exclui um agendamento.
 * 
 * @param {object} row - Objeto representando a linha.
 */
  async handleDeleteAgendamento(row: any) {

    await CndsStore.handleDeleteAgendamento(row.id)

  };

  /**
 * Renderiza o status do agendamento como uma tag estilizada.
 * 
 * @param {any} cell - Célula da tabela (não utilizado).
 * @param {object} row - Objeto representando a linha.
 * @returns {JSX.Element} - Tag estilizada com o status do agendamento.
 */
  statusFormatter(cell, row) {

    return getTag(row.status, row.tipo);

  }

  /**
 * Renderiza o status da certidão como uma tag estilizada.
 * 
 * @param {any} cell - Célula da tabela (não utilizado).
 * @param {object} row - Objeto representando a linha.
 * @returns {JSX.Element} - Tag estilizada com o status da certidão.
 */
  certidaoFormatter(cell, row) {
    switch (row.status_certidao) {
      case 0:
        if (row.tipo == "caixa"){
          return <Tag className="tagConfirmacao tagDownload " style={{ width: "70%", textAlign: "center" }}>Situação Regular</Tag>
        } else {
          return <Tag className="tagConfirmacao tagDownload " style={{ width: "70%", textAlign: "center" }}>Certidão Negativa</Tag>
        }
      case 1:
        return <Tag className="tagConfirmacao tagDownload tagNao" style={{ width: "70%", textAlign: "center" }}>Certidão Positiva</Tag>
      case 2:
        return <Tag className="tagConfirmacao tagDownload tagEfeitoNegativa" style={{ width: "70%", textAlign: "center" }}>Certidão Positiva com efeitos de Negativa</Tag>
      default:
        return <Tag className="tagConfirmacao tagDownload tagNovo" style={{ width: "70%", textAlign: "center" }}>NÃO DISPONÍVEL</Tag>
    }

  }

  /**
 * Formata uma data adicionando 3 horas e exibe a data e hora formatadas.
 * 
 * @param {string} cell - Data a ser formatada.
 * @param {object} row - Objeto representando a linha.
 * @returns {string} - Data e hora formatadas.
 */
  dateFormatter(cell, row) {

    if (cell) {

      return moment(cell).add(3, "hour").format("DD/MM/YYYY") + " às " + moment(cell).add(3, "hour").format("HH:mm");

    }

    return moment(row.inserted_at).add(3, "hour").format("DD/MM/YYYY") + " às " + moment(row.inserted_at).add(3, "hour").format("HH:mm");

  };

  /**
 * Renderiza um botão para visualizar os detalhes de uma linha.
 * 
 * @param {any} cell - Célula da tabela (não utilizado).
 * @param {object} row - Objeto representando a linha.
 * @returns {JSX.Element} - Botão para visualizar detalhes.
 */
  viewDetalhes = (cell, row) => {
    return (
      <MDBBtn
        disabled={!row._id}
        color="default"
        rounded
        size="sm"
        onClick={(e) => this.handleViewDetalhes(row)}
      >
        <img src="/images/view.png" width="18px" />
      </MDBBtn>
    );
  }

  /**
 * Renderiza um botão para visualizar os arquivos associados.
 * 
 * @param {any} cell - Célula da tabela (não utilizado).
 * @param {object} row - Objeto representando a linha.
 * @returns {JSX.Element} - Botão para baixar arquivos.
 */
  viewArquivos = (cell, row) => {
    return (
      <MDBBtn
        disabled={!row._id || (row.status != 2 && row.status != 4)}
        color="default"
        className="botaoDownload"
        rounded
        size="sm"
        onClick={(e) => this.handleViewArquivos(row)}
      >
        <img src="/images/download.svg" width="18px" />
      </MDBBtn>
    );
  }

  /**
 * Formata um valor de texto para exibição em letras maiúsculas.
 * 
 * @param {string} cell - Valor de texto a ser formatado.
 * @param {object} row - Objeto representando a linha.
 * @returns {string} - Texto em letras maiúsculas.
 */
  tipoFormat = (cell, row) => {

    if (cell) {

      return cell.toUpperCase();

    }

    return "";

  }

  /**
 * Exibe os detalhes de erros associados a uma linha.
 * 
 * @param {object} row - Objeto representando a linha.
 */
  handleViewDetalhes = async (row: any) => {
    this.obs.erros = row.error

    if (this.obs.erros != "" && this.obs.erros != null) {
      this.obs.view_erros = true
    } else {
      toast.warn("Nenhum erro encontrado!", {});
      return
    }
  };

  /**
 * Baixa os arquivos associados a uma linha.
 * 
 * @param {object} row - Objeto representando a linha.
 */
  handleViewArquivos = async (row: any) => {

    await CndsStore.downloadArquivos(row.cnds_id, this.obs.selectEmpresa.cnpj, row.tipo);

  };


  render() {

    const options = {
      searchField: this.createCustomSearchField,
    };

    let select_empresas = EmpresaStore.sortedEmpresas.map(map_emp => {

      let emp = EmpresaStore.getByCnpj(map_emp.cnpj);

      if (emp && emp.ativo) {

        return ({
          _id: emp._id,
          razao_social: emp.razao_social,
          ie: emp.inscricao_estadual,
          apelido: emp.apelido,
          cnpj_format: formatCnpj(emp.cnpj),
          cnpj: emp.cnpj
        })

      } else {

        return null;

      }

    }).filter(x => x != null)

    let empresaText = this.obs.selectEmpresa.cnpj != ""
      ? `${this.obs.selectEmpresa.razao_social.substring(0, 38)}... (${formatCnpj(this.obs.selectEmpresa.cnpj)})`
      : "Selecione uma empresa";

    let agendamentos = CndsStore.obs.index_agendamentos?.map(values => {
      return ({
        id: (values.id || values._id),
        tipo: values.recorrente ? 'SIM' : 'NÃO',
        recorrente: values.recorrente,
        data_solicitacao: new Date(`${values.mes}/${values.dia}/${values.ano}`),
        dia: values.dia,
        listEmpresas: values.cnpjs,
        itens: values,
        gratuito: values.gratuito,
        inserted_at: values.inserted_at
      })
    })

    let dayPickerProps = {
      localeUtils: MomentLocaleUtils,
      locale: "pt-br",
    };

    const selectRowProp = {
      mode: "checkbox",
      clickToSelect: true,
      columnWidth: "40px",
      onSelect: this.handleRowSelect,
      onSelectAll: this.handleRowSelectAll,
      selected: this.obs.agendamento.listEmpresas?.map(x => x.cnpj),
      bgColor: "rgba(27, 98, 111, 0.1)",
    };

    return (
      <>
        <MDBRow>

          <MDBCol md="4">
            <FormGroup className="empresaSelect" label="Empresa">
              <EmpresaSelect
                items={EmpresaStore.sortedEmpresas}
                itemRenderer={renderEmpresa}
                itemPredicate={filterEmpresa}
                onItemSelect={(e: IEmpresa) => this.handleBuscaTarefas(e)}
                inputProps={{ placeholder: "Filtrar uma empresa" }}
              >
                <Button
                  text={empresaText}
                  rightIcon="double-caret-vertical"
                  alignText={Alignment.LEFT} />
              </EmpresaSelect>
            </FormGroup>
          </MDBCol>

          <MDBCol md="2">
            <MDBBtn
              className="btnDefault btnBuscarNfe mt25"
              onClick={() => this.handleAtualizaTarefasPaged()}
            >
              Buscar
            </MDBBtn>
          </MDBCol>

        </MDBRow>

        <MDBRow>
          <MDBCol md="12" className="">
            <MDBBtn
              className="btnDefault btnBuscarNfe"
              onClick={(e) => this.handleOpenCreate()}
              style={{ marginRight: 10, float: "right" }}>
              Criar Solicitação
            </MDBBtn>

            <MDBBtn
              className="btnDefault btnBuscarNfe"
              style={{ marginRight: 10, float: "right" }}
              onClick={() => this.handleOpenAgendamentos()}>
              Agendamentos
            </MDBBtn>
          </MDBCol>
        </MDBRow>

        <div>
          <BootstrapTable className="TableDefault" ref="table" data={sortBy(CndsStore.obs.tasks_cnds, "inserted_at").reverse()}>
            <TableHeaderColumn dataField="_id" isKey={true} hidden width="1%">#</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.dateFormatter} dataField="inserted_at" width="20%">Data Solicitação</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.dateFormatter} dataField="_modifiedTS" width="20%">Ultima Atualização</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.tipoFormat} dataField="tipo" width="10%">Tipo</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.statusFormatter} width="30%">Status</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.certidaoFormatter} width="30%">Status Certidão</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.viewDetalhes} width="5%">Detalhes</TableHeaderColumn>
            <TableHeaderColumn dataFormat={this.viewArquivos} width="5%">Arquivo</TableHeaderColumn>
          </BootstrapTable>
        </div>

        <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>
          <Pager
            currentPage={this.obs.tarefas_page}
            totalCount={CndsStore.obs.tasks_length}
            pageSize={15}
            onPageChange={page => this.handleBuscaTarefasPaged(page)} />
        </div>

        {CndsStore.obs.open_solicitar && <SolicitaCnds />}

        <Alert
          confirmButtonText="Fechar"
          isOpen={this.obs.view_erros}
          onConfirm={() => this.onCloseErros()}
          onClose={() => this.onCloseErros()}
          canOutsideClickCancel={true}
          className="AlertUser"
          intent="primary"
        >
          <h3 className="titleProccess titleCnae">Logs Erros</h3>
          <br />
          <p>{this.obs.erros}</p>
        </Alert>

        <LoadingModal open={CndsStore.obs.loading_arquivos} onClose={() => CndsStore.obs.loading_arquivos = false} title={"Baixando arquivo"} />
        <LoadingModal open={CndsStore.obs.loading_tasks} onClose={() => CndsStore.obs.loading_tasks = false} title={"Buscando solicitações"} />

        <Dialog
          isOpen={this.obs.open_agendamentos}
          onClose={() => this.obs.open_agendamentos = false}
          className="AlertUser modal-agendamento"
        >
          <h2 className="titleSEFAZ">Agendamentos</h2>

          <div style={{ display: "flex" }}>
            <FormGroup label="Recorrência" />
            &nbsp;
            &nbsp;
            <Switch
              innerLabelChecked="Sim"
              innerLabel="Não"
              checked={CndsStore.obs.agendamentos_filter}
              onChange={(e: any) => {
                CndsStore.obs.agendamentos_filter = e.target.checked;
                this.handleFiltraAgendamentos();
              }}
            />
          </div>

          <div style={{ height: 400, overflow: "auto", marginTop: 10 }}>
            <BootstrapTable className="" data={agendamentos}>
              <TableHeaderColumn dataField="id" isKey={true} hidden width="1%">#</TableHeaderColumn>
              <TableHeaderColumn dataField="tipo" width="20%">Recorrente</TableHeaderColumn>
              <TableHeaderColumn dataFormat={this.formatAgendamento} dataField="data_solicitacao" width="20%">Dia agendamento</TableHeaderColumn>
              <TableHeaderColumn dataFormat={this.formatGratuito} dataField="gratuito" width="20%">Gratuito</TableHeaderColumn>
              <TableHeaderColumn dataFormat={this.buttonFormatterDetalhesAgend} width="15%">Detalhes</TableHeaderColumn>
            </BootstrapTable>
          </div>

          <MDBRow>
            <MDBCol md="10" className="page-sefa">
              <div style={{ marginTop: 5 }}>
                <Pager
                  currentPage={CndsStore.obs.page_agendamentos}
                  totalCount={CndsStore.obs.length_agendamentos}
                  pageSize={5}
                  onPageChange={page => this.handlePageAgendamento(CndsStore.obs.page_agendamentos = page)}
                />
              </div>
            </MDBCol>
            <MDBCol md="2">
              <Button text="Fechar" intent="primary" onClick={() => this.obs.open_agendamentos = false} style={{ marginTop: "30px", float: "right" }} />
            </MDBCol>
          </MDBRow>

        </Dialog>

        <Dialog
          isOpen={this.obs.open_edit_agendamento}
          onClose={() => this.handleCloseAgendamento()}
          className="modal-cnds"
        >

          <h2 className="titleSEFAZ">Editar Agendamento</h2>

          <div style={{ display: "flex" }}>
            <FormGroup label="Recorrência" />
            &nbsp;
            &nbsp;
            <Switch
              innerLabelChecked="Sim"
              innerLabel="Não"
              checked={this.obs.agendamento.recorrente}
              onChange={(e: any) => {
                this.obs.agendamento.recorrente = e.target.checked
                this.obs.agendamento.data_solicitacao = null
              }}
            />
          </div>

          <div style={{ display: "flex" }}>
            <FormGroup label="Gratuito" />
            &nbsp;
            &nbsp;
            <Switch
              innerLabelChecked="Sim"
              innerLabel="Não"
              checked={this.obs.agendamento.gratuito}
              disabled
            />
          </div>

          <div style={{ width: 300 }}>
            <FormGroup
              label={this.obs.agendamento.recorrente ? "Selecione o dia de recorrência" : "Selecione a data"}
            >
              <DateInput
                {...getMomentFormatter(this.obs.agendamento.recorrente ? "DD" : "DD/MM/YYYY")}
                locale="pt-br"
                dayPickerProps={dayPickerProps}
                placeholder={this.obs.agendamento.recorrente ? "DD" : "DD/MM/AAAA"}
                onChange={this.handleDateRealizarChange}
                canClearSelection
                value={this.obs.agendamento.data_solicitacao}
              />
            </FormGroup>
          </div>
          <FormGroup label="Empresas selecionadas:">
            <div className="select-empresa-cnds">
              <BootstrapTable data={select_empresas} selectRow={selectRowProp} options={options} search>
                <TableHeaderColumn dataField="cnpj" isKey={true} hidden width="1%">#</TableHeaderColumn>
                <TableHeaderColumn dataField="razao_social" width="60%">Razão Social</TableHeaderColumn>
                <TableHeaderColumn dataField="cnpj_format" width="30%">CNPJ</TableHeaderColumn>
              </BootstrapTable>
            </div>
          </FormGroup>

          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button text="Fechar" onClick={() => this.handleCloseAgendamento()} />
            <Button text="Salvar" intent="primary" onClick={() => this.handleUpdateTask()} />
          </div>
        </Dialog>

      </>
    )
  }
}
